import React, { useEffect, useState } from 'react';
import '../css/common.css';
import { API_BASE_URL, AUTH_LOCAL_KEYS, AUTH_STORAGE_KEYS } from '../config/api';
import { MOCK_MODE } from '../config/mock';

interface LoginProps {
  onLoginSuccess: (user: { username: string; email: string; role: string }) => void;
}

type LoginTokenResponse = {
  access_token: string;
  token_type: string;
  expires_in?: number;
  refresh_token?: string;
  username?: string;
};

type NewPasswordRequiredResponse = {
  challenge: 'NEW_PASSWORD_REQUIRED';
  session: string;
  username?: string;
};

type ErrorResponse = {
  detail?: string;
};

const Login: React.FC<LoginProps> = ({ onLoginSuccess }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [needsNewPassword, setNeedsNewPassword] = useState(false);
  const [challengeSession, setChallengeSession] = useState('');

  useEffect(() => {
    try {
      const expired = sessionStorage.getItem(AUTH_STORAGE_KEYS.expiredFlag);
      if (expired) {
        sessionStorage.removeItem(AUTH_STORAGE_KEYS.expiredFlag);
        setError('セッションの有効期限が切れました。もう一度ログインしてください。');
      }
    } catch {
      // ignore
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const trimmedUsername = username.trim();
      if (MOCK_MODE) {
        localStorage.setItem(AUTH_LOCAL_KEYS.token, 'mock-access-token');
        localStorage.setItem(AUTH_LOCAL_KEYS.username, trimmedUsername || 'mock-user');
        onLoginSuccess({
          username: trimmedUsername || 'mock-user',
          email: 'mock@example.com',
          role: 'user',
        });
        return;
      }

      if (needsNewPassword) {
        if (!password || !passwordConfirm) {
          throw new Error('新しいパスワードを入力してください。');
        }
        if (password !== passwordConfirm) {
          throw new Error('新しいパスワードが一致しません。');
        }
      }

      const endpoint = needsNewPassword
        ? `${API_BASE_URL}/api/auth/complete-new-password`
        : `${API_BASE_URL}/api/auth/login`;

      const body = needsNewPassword
        ? { username: trimmedUsername, session: challengeSession, new_password: password }
        : { username: trimmedUsername, password };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = (await response.json()) as
        LoginTokenResponse | NewPasswordRequiredResponse | ErrorResponse;

      if (!response.ok) {
        const detail =
          'detail' in data && typeof data.detail === 'string' && data.detail.trim()
            ? data.detail.trim()
            : '';
        throw new Error(detail || 'ログインに失敗しました');
      }

      if (!needsNewPassword && 'challenge' in data && data.challenge === 'NEW_PASSWORD_REQUIRED') {
        setNeedsNewPassword(true);
        setChallengeSession(String(data.session || ''));
        if (typeof data.username === 'string' && data.username.trim()) {
          setUsername(data.username.trim());
        }
        setPassword('');
        setPasswordConfirm('');
        setError('初回ログインです。新しいパスワードを設定してください。');
        return;
      }

      // Save token
      if (
        !('access_token' in data) ||
        typeof data.access_token !== 'string' ||
        !data.access_token.trim()
      ) {
        throw new Error('ログインに失敗しました');
      }
      localStorage.setItem(AUTH_LOCAL_KEYS.token, data.access_token);
      if (
        'refresh_token' in data &&
        typeof data.refresh_token === 'string' &&
        data.refresh_token.trim()
      ) {
        localStorage.setItem(AUTH_LOCAL_KEYS.refreshToken, data.refresh_token.trim());
      }
      const resolvedUsername =
        'username' in data && typeof data.username === 'string' && data.username.trim()
          ? data.username.trim()
          : trimmedUsername;
      if (resolvedUsername) {
        localStorage.setItem(AUTH_LOCAL_KEYS.username, resolvedUsername);
      }

      // Move on immediately; fetch /me in background to avoid "slow login" UX
      // when Cognito's GetUser API is temporarily slow.
      onLoginSuccess({ username: resolvedUsername, email: '', role: 'user' });

      void (async () => {
        try {
          const userResponse = await fetch(`${API_BASE_URL}/api/auth/me`, {
            headers: {
              Authorization: `Bearer ${data.access_token}`,
            },
          });
          if (!userResponse.ok) return;
          const userInfo = (await userResponse.json()) as {
            username: string;
            email: string;
            role: string;
          };
          onLoginSuccess(userInfo);
        } catch {
          // ignore background /me failures
        }
      })();
    } catch (err) {
      if (err instanceof Error && err.message) {
        setError(err.message);
      } else {
        setError('ユーザー名またはパスワードが正しくありません');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="login-container flex-center"
      style={{ minHeight: 'calc(100vh / var(--ui-scale, 1))', background: 'var(--bg-gradient)' }}
    >
      <div className="card" style={{ width: '100%', maxWidth: '400px' }}>
        <h1 className="login-title text-center mb-3">特許マップ作成システム</h1>
        <form onSubmit={handleSubmit}>
          <div className="mb-2">
            <label className="input-label">ユーザー名</label>
            <input
              type="text"
              className="input-field"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              disabled={needsNewPassword}
              required
            />
          </div>
          <div className="mb-3">
            <label className="input-label">
              {needsNewPassword ? '新しいパスワード' : 'パスワード'}
            </label>
            <input
              type="password"
              className="input-field"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          {needsNewPassword && (
            <div className="mb-3">
              <label className="input-label">新しいパスワード（確認）</label>
              <input
                type="password"
                className="input-field"
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
                required
              />
            </div>
          )}

          {error && <div className="input-error mb-2 text-center">{error}</div>}

          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? <span className="spinner"></span> : 'ログイン'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;
